import { Alert, BottomNavigation, BottomNavigationAction, Box, Button, ButtonBase, Chip, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Snackbar, SpeedDial, styled, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ArticleIcon from '@mui/icons-material/Article';
import EggAltIcon from '@mui/icons-material/EggAlt';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkIcon from '@mui/icons-material/Link';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Ingredient } from '../../../models/Ingredient';
import { Instruction } from "../../../models/Instruction";
import SaveIcon from '@mui/icons-material/Save';
import { Recipe } from "../../../models/Recipe";
import { addNewTag, getRecipeById, getRecipeImage, saveRecipe, uploadImage } from '../../../utils/RecipeService';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ImportHandoff } from "../../../models/ImportedRecipe";
import DeleteIcon from '@mui/icons-material/Delete';
import IngredientEdit from "./IngredientEdit";
import { IngredientType } from "../../../models/IngredientType";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ImageButton = styled(ButtonBase)(({ theme }) => ({
    position: 'relative',
    height: 200,
    [theme.breakpoints.down('sm')]: {
      width: '100% !important', // Overrides inline-style
      height: 100,
    },
    '&:hover, &.Mui-focusVisible': {
      zIndex: 1,
      '& .MuiImageBackdrop-root': {
        opacity: 0.15,
      },
      '& .MuiImageMarked-root': {
        opacity: 0,
      },
      '& .MuiTypography-root': {
        border: '4px solid currentColor',
      },
    },
}));

const ImageSrc = styled('span')({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center 40%',
});

const Image = styled('span')(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
}));
  
  const ImageBackdrop = styled('span')(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.palette.common.black,
    opacity: 0.4,
    transition: theme.transitions.create('opacity'),
}));
  
  const ImageMarked = styled('span')(({ theme }) => ({
    height: 3,
    width: 18,
    backgroundColor: theme.palette.common.white,
    position: 'absolute',
    bottom: -2,
    left: 'calc(50% - 9px)',
    transition: theme.transitions.create('opacity'),
}));
  

const RecipeEditor = () => {
    let {recipeId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useTranslation();
    const [value, setValue] = useState(0);
    const [name, setName] = useState<string | undefined>();
    const [portions, setPortions] = useState<number | undefined>();
    const [preparationTime, setPreparationTime] = useState<number | undefined>();
    const [source, setSource] = useState<string | undefined>();
    const [difficulty, setDifficulty] = useState<string | undefined>();
    const [preview, setPreview] = useState<string | undefined>('https://wiki.dave.eu/images/4/47/Placeholder.png');
    const [image, setImage] = useState();
    const [ingredients, setIngredients] = useState<IngredientType[]>([]);
    const [instructions, setInstructions] = useState<Instruction[]>([]);
    const [tags, setTags] = useState<string[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [importWarnings, setImportWarnings] = useState<string[] | undefined>();

    // Tags are only editable here while the recipe is new, which in practice means an import: the
    // editor can add tags after the recipe first gets an id, but it cannot remove them, and a chip
    // that silently reappears is worse than no chip at all. For a recipe that already exists, tag
    // editing stays on the details page, where TagView does both halves properly.
    const isNewRecipe: boolean = recipeId === undefined || recipeId === '-1';

    const mapToIngredientTypes = useCallback((ingredients: Ingredient[]) => {
        const newIngredients: Ingredient[] = [...ingredients];
        const ingredientTypes: IngredientType[] = [];
        let lastGroup: string | undefined = undefined;
        let lastKey: number = 0;
        
        newIngredients.forEach((ingredient: Ingredient, index: number) => {
            if(ingredient.group && ingredient.group !== lastGroup) {
                lastGroup = ingredient.group;
                ingredientTypes.push({type: 'Group', name: ingredient.group, key: lastKey, id: null});
                lastKey = lastKey + 1;
            }
            ingredientTypes.push({id: ingredient.id, name: ingredient.name, amount: ingredient.amount, unit: ingredient.unit, type: 'Ingredient', key: lastKey});
            lastKey = lastKey + 1;
        });

        return ingredientTypes;
    }, []);

    const applyRecipe = useCallback((recipe: Recipe) => {
        setName(recipe.name);
        setPortions(recipe.yield);
        setPreparationTime(recipe.cookTime);
        setSource(recipe.source);
        setDifficulty(recipe.difficulty);
        setIngredients(mapToIngredientTypes([...recipe.ingredients].sort((i1, i2) => i1.orderNumber - i2.orderNumber)));
        setInstructions(recipe.instructions);
        setTags(recipe.tags ? recipe.tags : []);
    }, [mapToIngredientTypes]);

    useEffect(() => {
        // An import hands the extracted recipe over through the router's location state and this
        // editor doubles as the review screen for it. Nothing has been saved yet - the user
        // corrects whatever the extraction got wrong and saves it like any other new recipe.
        const handoff = location.state as ImportHandoff | null;
        if(recipeId === '-1' && handoff && handoff.importedRecipe) {
            applyRecipe(handoff.importedRecipe);
            setImportWarnings(handoff.warnings && handoff.warnings.length > 0 ? handoff.warnings : undefined);
            if(handoff.importedImage) {
                setImage(handoff.importedImage as any);
                setPreview(URL.createObjectURL(handoff.importedImage));
            }
            return;
        }

        if(recipeId !== undefined && recipeId !== null && recipeId !== '-1') {
            getRecipeById(recipeId).then((recipe: Recipe) => {
                if(recipe !== undefined && recipe !== null) {
                    applyRecipe(recipe);
                }
            });
            getRecipeImage(recipeId as any as number).then((image: Blob | null) => {
                if(image) {
                    setPreview(URL.createObjectURL(image));
                }
            });
        }
    }, [applyRecipe, location.state, recipeId]);

    const changeDifficulty = useCallback((difficulty: string) => {
        setDifficulty(difficulty);
    }, [setDifficulty])

    const handleUploadClick = (event: any) => {
        const file = event.target.files[0];
        if(file !== undefined) {
            setPreview(URL.createObjectURL(file));
            setImage(file);
        }
    };

    const addNewInstruction = useCallback(() => {
        setInstructions((currentInstructions: Instruction[]) => {
            const newInstructions = [...currentInstructions];
            newInstructions.push({id: null, description: '', orderNumber: currentInstructions.length + 1, groupName: undefined});
            return newInstructions;
        });
    }, [setInstructions]);

    const changeInstruction = useCallback((index: number, description: string) => {
        setInstructions((currentInstructions: Instruction[]) => {
            const newInstructions: Instruction[] = [...currentInstructions];
            const changedInstruction: Instruction | undefined = newInstructions[index];
            changedInstruction.description = description;
            newInstructions[index] = changedInstruction;
            return newInstructions;
        });
    }, [setInstructions]);

    const deleteInstruction = useCallback((index: number) => {
        setInstructions((currentInstructions: Instruction[]) => {
            const newInstructions: Instruction[] = [...currentInstructions];
            newInstructions.splice(index, 1);
            return newInstructions.map((instruction, index) => {
                instruction.orderNumber = index + 1;
                return instruction;
            });
        });
    }, [setInstructions]);

    const saveNewRecipe = useCallback(() => {
        // Build JSON from all data
        const recipe: Recipe = {
            id: recipeId && recipeId !== '-1' ? recipeId as any as number : null, 
            name: name ? name : '', 
            yield: portions ? portions : 1,
            cookTime: preparationTime ? preparationTime: 0,
            source: source,
            difficulty: difficulty,
            ingredients: mapToIngredients(ingredients),
            instructions: instructions,
            tags: tags
        }

        // saveRecipe ignores the tags field, so they have to be added one call at a time once the
        // recipe has an id. Only for a new recipe - see isNewRecipe above. Imported tags are capped
        // server-side, which keeps this bounded.
        const saveTags = (savedRecipe: Recipe) =>
            isNewRecipe
                ? Promise.all(tags.map(tag => addNewTag(savedRecipe.id!, tag))).catch(() => undefined)
                : Promise.resolve();

        // Send request to server
        saveRecipe(recipe).then((savedRecipe: Recipe) => {
            if(savedRecipe === null) {
                setSnackbarOpen(true);
                return;
            }

            saveTags(savedRecipe).then(() => {
                if(image !== undefined) {
                    // Upload image for recipe
                    uploadImage(savedRecipe.id!, image).then(response => {
                        if(response.ok) {
                            navigate('/recipe/' + savedRecipe.id);
                        } else {
                            setSnackbarOpen(true);
                        }
                    });
                } else {
                    navigate('/recipe/' + savedRecipe.id);
                }
            });
        });
    }, [recipeId, isNewRecipe, name, portions, preparationTime, source, difficulty, ingredients, instructions, tags, image, navigate]);

    const mapToIngredients = (ingredientTypes: IngredientType[]) => {
        const ingredients: Ingredient[] = [];
        let ingredientGroup: string | undefined = undefined;
        let currentOrderNumber: number = 1;
        ingredientTypes.forEach((ingredientType: IngredientType) => {
            if(ingredientType.type === 'Ingredient') {
                ingredients.push({id: ingredientType.id, name: ingredientType.name, amount: ingredientType.amount, unit: ingredientType.unit, group: ingredientGroup, orderNumber: currentOrderNumber});
                currentOrderNumber = currentOrderNumber + 1;
            } else if(ingredientType.type === 'Group') {
                ingredientGroup = ingredientType.name;
            }
        });
        return ingredients;
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '10px', height: '100%'}}>
            {importWarnings && (
                <Alert severity="info" onClose={() => setImportWarnings(undefined)}
                       sx={{ width: '100%', maxWidth: 800, mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{t('importReviewHint')}</Typography>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '18px' }}>
                        {importWarnings.map((warning, index) => (
                            <li key={index}><Typography variant="body2">{warning}</Typography></li>
                        ))}
                    </ul>
                </Alert>
            )}
            {value === 0 && (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '5px', height: 'calc(100% - 150px)', overflowY: 'auto'}}>
                    <ImageButton focusRipple key={'Image'} style={{width: '350px', height: '300px'}}>
                        <ImageSrc style={{ backgroundImage: `url(${preview})` }} />
                        <ImageBackdrop className="MuiImageBackdrop-root" />
                        <Image>
                            <Typography component="label" variant="subtitle1" color="inherit" sx={{position: 'relative', p: 4, pt: 2, pb: (theme) => `calc(${theme.spacing(1)} + 6px)`}}>
                                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}><CloudUploadIcon style={{marginRight: '5px'}} /> {t('uploadImage')}</div>
                                <ImageMarked className="MuiImageMarked-root" />
                                <VisuallyHiddenInput type="file" accept="image/*" onChange={handleUploadClick} />
                            </Typography>
                        </Image>
                    </ImageButton>

                    <TextField 
                        id="name" 
                        label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}><div style={{marginLeft: '5px'}}>{t('name')}</div></Box>} 
                        variant="outlined"
                        sx={{width: '100%', marginTop: '10px'}}
                        value={name ? name : ''}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        id="portions"
                        label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}><RestaurantIcon /> <div style={{marginLeft: '5px'}}>{t('portions')}</div></Box>}
                        type="number"
                        style={{marginTop: '10px'}}
                        InputProps={{
                            inputProps: { min: 1 }
                        }}
                        sx={{width: '100%'}}
                        value={portions ? portions : ''}
                        onChange={(e) => setPortions(e.target.value as any as number)}
                    />
                    <TextField
                        label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}><AccessTimeIcon /> <div style={{marginLeft: '5px'}}>{t('cookTime')}</div></Box>}
                        id="cooktime"
                        type="number"
                        sx={{ width: '100%', marginTop: '10px' }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">Min.</InputAdornment>,
                            inputProps: { min: 1 }
                        }}
                        value={preparationTime ? preparationTime : ''}
                        onChange={(e) => setPreparationTime(e.target.value as any as number)}
                    />
                    <TextField
                        label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}><LinkIcon /> <div style={{marginLeft: '5px'}}>{t('source')}</div></Box>}
                        id="source"
                        sx={{ width: '100%', marginTop: '10px' }}
                        value={source ? source : ''}
                        onChange={(e) => setSource(e.target.value)}
                    />
                    <FormControl fullWidth style={{marginTop: '10px', width: '100%'}}>
                        <InputLabel id="demo-simple-select-label"><Box display={'flex'} flexDirection={'row'} alignItems={'center'}><PriorityHighIcon /> <div style={{marginLeft: '5px'}}>{t('difficulty')}</div></Box></InputLabel>
                        <Select id="language-select" label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}><PriorityHighIcon /> <div style={{marginLeft: '5px'}}>{t('difficulty')}</div></Box>} value={difficulty ? difficulty : ''} onChange={(event) => changeDifficulty(event.target.value)}>
                            <MenuItem value={'EASY'}><Box display={'flex'} flexDirection={'row'} alignItems={'center'}><div>{t('easy')}</div></Box></MenuItem>
                            <MenuItem value={'MEDIUM'}><Box display={'flex'} flexDirection={'row'} alignItems={'center'}><div>{t('medium')}</div></Box></MenuItem>
                            <MenuItem value={'HARD'}><Box display={'flex'} flexDirection={'row'} alignItems={'center'}><div>{t('hard')}</div></Box></MenuItem>
                        </Select>
                    </FormControl>

                    {isNewRecipe && tags.length > 0 && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">{t('tags')}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                {/* Imported keywords are frequently a site's SEO terms rather than
                                    useful tags, so they are shown here to be pruned before saving. */}
                                {tags.map((tag) => (
                                    <Chip key={tag} label={tag} size="small"
                                          onDelete={() => setTags(current => current.filter(t2 => t2 !== tag))} />
                                ))}
                            </Box>
                        </Box>
                    )}
                </div>
            )}
            {value === 1 && (
                <IngredientEdit ingredients={ingredients} updateIngredients={setIngredients} allowGroupAdding={true} simplifiedEdit={false}/>
            )}
            {value === 2 && (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '10px', width: '100%', height: 'calc(100% - 150px)', overflowY: 'auto', overflowX: 'hidden'}}>
                    {instructions.sort((i1, i2) => i1.orderNumber - i2.orderNumber).map((instruction, index) => {
                        return (
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '10px', marginLeft: '15px', marginRight: '5px', width: '100%', justifyContent: 'center'}}>
                                <TextField
                                    id="instruction"
                                    label={t('instruction')}
                                    multiline
                                    rows={3}
                                    style={{width: '100%', maxWidth: '800px'}}
                                    value={instruction.description ? instruction.description : ''}
                                    onChange={(e) => changeInstruction(index, e.target.value)}
                                />
                                <IconButton aria-label="delete" size="medium" onClick={() => deleteInstruction(index)}>
                                    <DeleteIcon fontSize="inherit" />
                                </IconButton>
                            </div>
                        );
                    })}
                    
                    <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '10px'}}>
                        <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addNewInstruction}>{t('newInstruction')}</Button>
                    </div>
                </div>
            )}

            <SpeedDial ariaLabel="save" sx={{ position: 'absolute', bottom: 75, right: 30 }} icon={<SaveIcon />} onClick={saveNewRecipe}/>

            <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
                <BottomNavigation showLabels value={value} onChange={(event, newValue) => {
                    setValue(newValue);
                }}>
                    <BottomNavigationAction label={t('info')} icon={<ArticleIcon />} />
                    <BottomNavigationAction label={t('ingredients')} icon={<EggAltIcon />} />
                    <BottomNavigationAction label={t('instructions')} icon={<RestaurantIcon />} />
                </BottomNavigation>
            </Paper>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={(e) => setSnackbarOpen(false)}
            >
                 <Alert onClose={(e) => setSnackbarOpen(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
                    {t('saveFailure')}
                 </Alert>
            </Snackbar>
        </div>
    );
};

export default RecipeEditor;